/*
 *   File : filter.js
 *   Author : https://evoqins.com
 *   Description : filter component
 *   Integrations : NA
 *   Version : 1.1.0
 */
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { PrimaryButton } from "../Buttons";
import Icon from "../icons";

/** ---------- Overlay Portal ---------- **/
const BodyOverlay = ({ onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => (document.body.style.overflow = prev);
  }, []);

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.4)", zIndex: 1040 }}
      onClick={onClose}
    />,
    document.body,
  );
};

// Helper function to format title from snake_case to Title Case
const formatTitle = (str) => {
  if (str == null || typeof str !== "string")
    return str != null ? String(str) : "";
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const Filter = forwardRef(function Filter(
  {
    filters = [],
    onApply,
    filterName = "Filters",
    initialSelections = null,
  } = {},
  ref,
) {
  const [open, setOpen] = useState(false);
  const [numOfSelectedFilters, setNumOfSelectedFilters] = useState(0);

  // Transform backend data: array of { title, type: "icon"|"checkbox", options: [{ label, value }] }
  const transformedFilters = useMemo(() => {
    let list = [];
    if (Array.isArray(filters)) {
      list = filters;
    } else if (filters && typeof filters === "object") {
      list =
        filters.filters ??
        filters.data ??
        filters.items ??
        filters.filter_options ??
        filters.users ??
        filters.sub_admins ??
        filters.orders ??
        [];
      if (!Array.isArray(list)) list = [];
    }
    if (list.length === 0) return [];

    return list.map((filter, index) => {
      const rawTitle = filter?.title ?? filter?.id ?? `filter_${index}`;
      return {
        id: rawTitle,
        title: formatTitle(rawTitle),
        type: filter.type || "checkbox",
        options: (filter.options || []).map((opt) => ({
          id: opt.value !== undefined ? opt.value : opt.id,
          label: opt.label,
          icon: opt.icon,
          value: opt.value !== undefined ? opt.value : opt.id,
        })),
      };
    });
  }, [filters]);

  const [activeLabelId, setActiveLabelId] = useState(
    transformedFilters[0]?.id || null,
  );

  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);

  const [selections, setSelections] = useState(() => {
    const initial = {};
    transformedFilters.forEach((f) => {
      // if a saved value exists for this filter key, use it — else default
      initial[f.id] =
        initialSelections?.[f.id] !== undefined
          ? initialSelections[f.id]
          : f.type === "icon"
            ? null
            : [];
    });
    return initial;
  });

  // ─── Imperative clear API exposed to parent via ref ───────────────────────
  useImperativeHandle(
    ref,
    () => ({
      clear() {
        const cleared = {};
        transformedFilters.forEach((f) => {
          cleared[f.id] = f.type === "icon" ? null : [];
        });
        setSelections(cleared);
        onApply?.({});
      },
    }),
    [transformedFilters, onApply],
  );
  // ──────────────────────────────────────────────────────────────────────────

  // Update active label when filters change
  useEffect(() => {
    if (transformedFilters.length > 0 && !activeLabelId) {
      setActiveLabelId(transformedFilters[0].id);
    }
  }, [transformedFilters, activeLabelId]);

  // Reset selections when filters change
  useEffect(() => {
    const initial = {};
    transformedFilters.forEach((f) => {
      initial[f.id] =
        initialSelections?.[f.id] !== undefined
          ? initialSelections[f.id]
          : f.type === "icon"
            ? null
            : [];
    });
    setSelections(initial);
  }, [transformedFilters]); // intentionally excludes initialSelections — only runs when filter structure changes

  useEffect(() => {
    setNumOfSelectedFilters(getSelectedCount(selections));
  }, [selections]);

  // Update position on resize/scroll while open
  useEffect(() => {
    if (!open) return;

    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  // Click-outside to close (global listener only when open)
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        !document.querySelector(".e-filter-portal")?.contains(e.target)
      ) {
        setOpen(false);
      }
    }

    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Handlers
  function handleChooseLabel(id) {
    setActiveLabelId(id);
  }

  function handleSelectIcon(labelId, optionValue) {
    setSelections((prev) => ({
      ...prev,
      [labelId]: prev[labelId] === optionValue ? null : optionValue,
    }));
  }

  function handleToggleCheckbox(labelId, optionValue) {
    setSelections((prev) => {
      const arr = Array.isArray(prev[labelId]) ? prev[labelId] : [];
      const exists = arr.includes(optionValue);
      return {
        ...prev,
        [labelId]: exists
          ? arr.filter((i) => i !== optionValue)
          : [...arr, optionValue],
      };
    });
  }

  function handleClear() {
    const cleared = {};
    transformedFilters.forEach((f) => {
      cleared[f.id] = f.type === "icon" ? null : [];
    });
    setSelections(cleared);
    onApply?.({});
  }

  function handleApply() {
    const transformedSelections = {};
    Object.keys(selections).forEach((key) => {
      const value = selections[key];
      if (
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        transformedSelections[key] = value;
      }
    });
    onApply?.(transformedSelections);
    setOpen(false);
  }

  const getSelectedCount = (filtersObj) =>
    Object.values(filtersObj).filter((value) => {
      if (value === null || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return Boolean(value);
    }).length;

  const activeFilter =
    transformedFilters.find((f) => f.id === activeLabelId) ||
    transformedFilters[0];

  // Compute a local pos immediately so portal renders on first paint
  const immediatePos =
    dropdownPos ||
    (triggerRef.current
      ? (() => {
          const rect = triggerRef.current.getBoundingClientRect();
          return {
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
          };
        })()
      : null);

  // Don't render if no filters
  if (!transformedFilters || transformedFilters.length === 0) {
    return null;
  }

  return (
    <>
      {open && <BodyOverlay onClose={() => setOpen(false)} />}

      <div
        className="e-filter-wrapper rounded-16 position-relative"
        ref={triggerRef}
      >
        <PrimaryButton
          name={filterName}
          ClassName={`py-2 px-3 rounded-16 text-eerie-black fs-14 fw-400 flex-row-reverse d-flex column-gap-2 align-items-center
            ${
              open
                ? "border-facebook-blue"
                : numOfSelectedFilters !== 0
                  ? "border-facebook-blue border-opacity-10 bg-facebook-blue bg-opacity-4"
                  : "bg-black-4x"
            }
          `}
          onPress={() => setOpen((p) => !p)}
          icon="filter-lines"
        />

        {numOfSelectedFilters !== 0 && !open && (
          <div className="e-filter-count position-absolute">
            {numOfSelectedFilters}
          </div>
        )}
      </div>

      {open &&
        immediatePos &&
        createPortal(
          <div
            className={`e-filter-body e-filter-portal d-flex flex-column w-328 bg-white-w-opacity e-drop-menu bg-opacity-43 rounded-30 p-1 ${
              open ? "open" : ""
            }`}
            style={{
              position: "fixed",
              top: immediatePos.top,
              right: immediatePos.right,
              zIndex: 1055,
            }}
          >
            <div className="border-bottom border-white d-flex justify-content-between align-items-center p-24">
              <p className="mb-0 fs-16 fw-500 text-eerie-black lh-1">Filters</p>
              <PrimaryButton
                icon="close"
                ClassName="bg-transparent p-0"
                onPress={() => setOpen(false)}
              />
            </div>

            <div className="row">
              <div className="col-12">
                <div className="row border-bottom border-white">
                  <div className="col-5 ps-1 pe-0">
                    <div className="d-flex flex-column py-8">
                      {transformedFilters.map((f) => {
                        const isActive = f.id === activeLabelId;
                        return (
                          <button
                            key={f.id}
                            type="button"
                            className={`text-start e-filter-option p-12 border-0 d-flex align-items-center justify-content-between ${
                              isActive
                                ? "bg-facebook-blue-4 bg-opacity-4 text-facebook-blue"
                                : "text-eerie-black"
                            }`}
                            onClick={() => handleChooseLabel(f.id)}
                          >
                            <div className="fs-14 fw-400 lh-24">{f.title}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="col-7 p-0">
                    <div className="d-flex flex-column border-start h-100 border-white">
                      {activeFilter && activeFilter.type === "icon"
                        ? activeFilter.options.map((opt) => {
                            const isSelected =
                              selections[activeFilter.id] === opt.value;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                className="d-flex e-filter-option-value align-items-center bg-transparent border-0 p-12"
                                onClick={() =>
                                  handleSelectIcon(activeFilter.id, opt.value)
                                }
                              >
                                <Icon
                                  icon={isSelected ? "selected" : "unselect"}
                                  size={16}
                                />
                                <span className="fs-12 fw-400 lh-24 ms-2">
                                  {opt.label}
                                </span>
                              </button>
                            );
                          })
                        : activeFilter &&
                          activeFilter.options.map((opt) => {
                            const checked =
                              Array.isArray(selections[activeFilter.id]) &&
                              selections[activeFilter.id].includes(opt.value);

                            return (
                              <label
                                key={opt.id}
                                className="d-flex align-items-center e-filter-option-value column-gap-2 p-12"
                              >
                                <input
                                  type="checkbox"
                                  style={{ accentColor: "#1877F2" }}
                                  checked={checked}
                                  onChange={() =>
                                    handleToggleCheckbox(
                                      activeFilter.id,
                                      opt.value,
                                    )
                                  }
                                />
                                <span className="fs-12 fw-400 lh-24">
                                  {opt.label}
                                </span>
                              </label>
                            );
                          })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex column-gap-2 p-3">
              <PrimaryButton
                name="Clear all"
                ClassName="px-border py-12 bg-transparent w-100 rounded-pill text-midnight-blue fs-16 fw-400 lh-24 border-midnight-blue"
                onPress={handleClear}
              />
              <PrimaryButton
                name="Apply"
                ClassName="fs-16 fw-400 py-12 w-100 lh-24 text-white rounded-pill bg-midnight-blue"
                onPress={handleApply}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

export default Filter;
